import React, {useState, useEffect} from "react";
import useSWR, {mutate} from "swr";
import {
    Box,
    Footer,
    FormField,
    List,
    MaskedInput,
    ResponsiveContext,
    Text,
    TextInput,
} from "grommet";
import {useRouter} from "next/router";
import {handleErrors} from "../../utils/errors";
import StatusMessage from "../../components/StatusMessage";
import Section from "../../components/Section";
import NextAnchor from "../../components/NextAnchor";
import Drawer from "../../components/Drawer";
import Form from "../../components/Form";
import {getPhoneMask} from "../../utils/phone";
import {validatePhoneNumber, validatePostalCode} from "../../lib/validation";
import SelectTeamMember from "../../components/pages/settings/SelectTeamMember";
import {useSession} from "next-auth/react";
import {
    IconPlus,
    IconUsers,
    IconMapPin as IconLocation
} from '@tabler/icons-react';
import Icon from "../../components/Icon";
import StateInput from "../../components/StateInput";
import CountryInput from "../../components/CountryInput";
import HiddenFormField from "../../components/HiddenFormField";
import {getStateOptions} from "../../utils/states";
import {debounce} from "../../utils/debounce";
import {SessionToken, AddressAutofillCore} from '@mapbox/search-js-core';
import TeamMemberSection from "../../components/settings/account/TeamMemberSection";
import BusinessDetailsSection from "../../components/settings/account/BusinessDetailsSection";
import AppLayout from "../../components/layouts/AppLayout";
import InviteTeammateDrawer from "../../components/settings/InviteTeammateDrawer";
import SubMenu from "../../components/SubMenu";
import { SETTINGS_PAGE_ACCOUNT_LINKS } from "../../constants/Links";

const AccountSettings = (props) => {
    const {showNotification } = props;

    const {data: session} = useSession();
    const {user} = session || {};

    const router = useRouter();
    const { action } = router.query;

    const fetcher = (url) =>
        fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .catch(showNotification);

    const { data } = useSWR("/api/settings/account", fetcher, {
        revalidateOnFocus: false,
    });

    const { data: teamData } = useSWR(
        `/api/settings/team`,
        fetcher,
        { revalidateOnFocus: false }
    );

    return (
        <AppLayout title="Account" hasStickyDesktopPageHeader>
            <Box width={{ max: 'large' }} flex="grow">
                <SubMenu items={SETTINGS_PAGE_ACCOUNT_LINKS} />
                <BusinessDetailsSection isLoading={!data} data={data} user={user} />
                <TeamDetailsSection
                    user={user}
                    team={teamData}
                    showNotification={showNotification}
                />
                <Footer />
            </Box>
            {action && action === "edit" && (
                <Drawer
                    title={`Edit business details`}
                    onClose={() => router.push("/settings", undefined, { scroll: false })}
                >
                    <UpdateBusinessDetailsForm
                        details={data}
                        teamData={teamData}
                        onSuccess={() => mutate("/api/settings/account")} // refresh page when bank account details change
                        {...props}
                    />
                </Drawer>
            )}
        </AppLayout>
    );
}

const TeamDetailsSection = ({ user, team, showNotification }) => {
    const [showInviteTeammateDrawer, setShowInviteTeammateDrawer] = useState(false);
    const members = (team?.users || [])
        .map((member) => ({
            ...member,
            isUser: user && (member.email === user.email),
        }))
        .sort(({ isUser: a }, { isUser: b }) => {
            // current user should always be the first in the list
            if (a) return -1;
            if (b) return 1;
            return 0;
        });

    return (
        <Section
            title="Team"
            icon={IconUsers}
            // Rows pad themselves so the list dividers run the full section width
            contentProps={{pad: {bottom: "xsmall"}}}
            control={
                <NextAnchor
                    onClick={() => setShowInviteTeammateDrawer(true)}
                    label={
                        <Icon SelectedIcon={IconPlus} size="small" />
                    }
                    tip={"Invite teammate"}
                    button
                />
            }
        >
            <Box pad={{horizontal: "small"}} skeleton={ !members ? {animation: 'fadeIn'} : undefined }>
                <Text width="small"/>
                <Text width="medium"/>
            </Box>
            <List pad={{vertical: "xxsmall", horizontal: "small"}} data={members}>
                {(member) => (
                    <TeamMemberSection
                        member={member}
                        showPermissionSetting={user.owner}
                        showNotification={showNotification}
                    />
                )}
            </List>
            {showInviteTeammateDrawer && (
                <InviteTeammateDrawer
                    onClose={() => setShowInviteTeammateDrawer(false)}
                    onSuccess={() => mutate('/api/settings/team')}
                />
            )}
        </Section>
    );
};

const UpdateBusinessDetailsForm = (props) => {
    const {
        details,
        teamData,
        onSuccess,
    } = props;
    const { business_name, phone_number, website, primary_contact, address } = details || {};
    const router = useRouter();
    const [formValues, setFormValues] = useState({
        business_name,
        phone_number,
        website,
        'address_attributes[id]': address?.id,
        'address_attributes[address1]': address?.address1 || '',
        'address_attributes[address2]': address?.address2 || '',
        'address_attributes[city]': address?.city || '',
        'address_attributes[state]': address?.state || '',
        'address_attributes[postal_code]': address?.postal_code || '',
        'address_attributes[country]': address?.country || 'US',
    });
    const [updateError, setUpdateError] = useState();
    const [stateOptions, setStateOptions] = useState(getStateOptions(address?.country || 'US'));
    const isUS = () => formValues['address_attributes[country]'] === 'US';

    // Mapbox address autofill - initialize client side only to avoid SSR issues
    const [autofill, setAutofill] = useState(null);
    const [addressSuggestions, setAddressSuggestions] = useState([]);

    useEffect(() => {
        const core = new AddressAutofillCore({ accessToken: process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN });
        setAutofill(core);
    }, []);

    const getAddressSuggestions = (e) => {
        const hasAtLeastTwoLetters = value => /[a-zA-Z].*[a-zA-Z]/.test(value);
        const value = e.target.value || '';
        if (value.length > 3 && hasAtLeastTwoLetters(value)) {
            const sessionToken = new SessionToken();
            autofill?.suggest(value, { sessionToken }).then((response) => {
                setAddressSuggestions(response.suggestions.map(suggestion => ({
                    label: (
                        <Box pad={{ vertical: "xsmall", horizontal: "small" }} direction="row" gap="xsmall" align="center">
                            <Icon SelectedIcon={IconLocation} size="small" />
                            <Box>
                                <Text size="small">{suggestion.address_line1}</Text>
                                <Text truncate={true} size="xsmall" color="text-weak">{suggestion.description}</Text>
                            </Box>
                        </Box>
                    ),
                    value: suggestion.address_line1,
                    address: suggestion,
                })) || []);
            }).catch(() => {
                setAddressSuggestions([]);
            });
        }
    };

    const onSelectAddress = ({ suggestion }) => {
        const selected = suggestion.address;
        const country = selected?.country_code?.toUpperCase() || 'US';
        setStateOptions(getStateOptions(country));
        setFormValues({
            ...formValues,
            'address_attributes[address1]': selected?.address_line1,
            'address_attributes[address2]': selected?.address_line2 || '',
            'address_attributes[city]': selected?.address_level2,
            'address_attributes[state]': selected?.region_code?.toUpperCase(),
            'address_attributes[postal_code]': selected?.postcode,
            'address_attributes[country]': country,
        });
        setAddressSuggestions([]);
    };

    const saveBusinessDetails = ({ value, callback }) => {
        // Convert bracket-notation keys (e.g. "address_attributes[address1]")
        // into nested objects for Rails
        const body = {};
        for (const [key, val] of Object.entries(value)) {
            const match = key.match(/^(\w+)\[(\w+)\]$/);
            if (match) {
                const [, parent, child] = match;
                body[parent] = body[parent] || {};
                body[parent][child] = val;
            } else {
                body[key] = val;
            }
        }

        fetch("/api/settings/account", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        })
            .then(handleErrors)
            .then((data) => {
                if (callback) callback(data);
                return data;
            })
            .then(
                props.showNotification({ message: "Business details updated" })
            )
            .catch(setUpdateError);
    };
    const phoneMask = getPhoneMask('US');

    return (
        <ResponsiveContext.Consumer>
            {(size) => (
                <>
                    {updateError && <StatusMessage apiError={updateError} />}
                    <Form
                        onReset={() => router.push("/settings")}
                        onSubmit={(event) =>
                            saveBusinessDetails({
                                value: event.value,
                                callback: () => {
                                    router.push("/settings", undefined, { scroll: false });
                                    onSuccess();
                                },
                            })
                        }
                        onChange={(nextValue, { touched }) => {
                            setUpdateError(undefined);
                            // address1 becomes an object when chosen from suggestions
                            if (nextValue?.['address_attributes[address1]'] && typeof nextValue['address_attributes[address1]'] !== "string") {
                                nextValue['address_attributes[address1]'] = nextValue['address_attributes[address1]'].value;
                            }
                            if (touched['address_attributes[country]']) {
                                setStateOptions(getStateOptions(nextValue['address_attributes[country]']));
                            }
                            setFormValues(nextValue);
                        }}
                        value={formValues}
                        editOnly
                        sectionProps={{
                            margin: "none",
                            border: false,
                            width: size === "small" ? "auto" : "medium",
                            pad: "none",
                            headerProps: {
                                border: false,
                            },
                        }}
                        validate="blur"
                    >
                        <FormField
                            label="Doing business as (DBA)"
                            placeholder="Business name"
                            name="business_name"
                            component={TextInput}
                            required
                        />
                        {address?.id && (
                            <HiddenFormField name="address_attributes[id]" hidden />
                        )}
                        <FormField
                            label="Business address"
                            placeholder="Street address"
                            name="address_attributes[address1]"
                            suggestions={addressSuggestions}
                            onSuggestionSelect={debounce(onSelectAddress, 300)}
                            onChange={getAddressSuggestions}
                            autoComplete="chrome-off"
                            required
                        />
                        <FormField
                            placeholder="Suite/Unit/Apt"
                            name="address_attributes[address2]"
                            component={TextInput}
                            autoComplete="chrome-off"
                        />
                        <FormField
                            placeholder="City"
                            label="City"
                            name="address_attributes[city]"
                            component={TextInput}
                            required
                            autoComplete="chrome-off"
                        />
                        <Box direction="row" gap="small">
                            <Box basis="1/2">
                                <FormField
                                    label="Country"
                                    name="address_attributes[country]"
                                    component={CountryInput}
                                    placeholder="Country"
                                    autoComplete="chrome-off"
                                    required
                                    plain
                                />
                            </Box>
                            {stateOptions && stateOptions.length > 0 && (
                                <Box basis="1/2">
                                    <FormField
                                        label={isUS() ? "State" : "Region"}
                                        name="address_attributes[state]"
                                        component={StateInput}
                                        stateOptions={stateOptions}
                                        placeholder={isUS() ? "State" : "Region"}
                                        autoComplete="chrome-off"
                                        required={isUS()}
                                        plain
                                    />
                                </Box>
                            )}
                        </Box>
                        <FormField
                            label="Postal code"
                            placeholder="Postal code"
                            name="address_attributes[postal_code]"
                            component={TextInput}
                            validate={isUS() ? validatePostalCode : undefined}
                            required={isUS()}
                            autoComplete="chrome-off"
                        />
                        <Box margin={{ top: "small" }} />
                        <FormField
                            label="Phone Number"
                            name="phone_number"
                            component={MaskedInput}
                            validate={validatePhoneNumber}
                            placeholder={phoneMask.placeholder}
                            mask={phoneMask.formatted}
                        />
                        <FormField
                            label="Website"
                            name="website"
                            placeholder="www.website.com"
                            component={TextInput}
                        />
                        <Box margin={{ top: "small" }} />
                        <FormField
                            label="Primary contact"
                            name="primary_contact_id"
                            margin={{bottom: 'none'}}
                        >
                            <SelectTeamMember
                                plain
                                placeholder="Select primary contact"
                                teamMembers={teamData.users}
                                currentlySelectedId={primary_contact?.id || ''}
                                name="primary_contact_id"
                                margin={{bottom: 'none'}}
                                label="Primary Contact"
                            />
                        </FormField>
                    </Form>
                </>
            )}
        </ResponsiveContext.Consumer>
    );
};

export default AccountSettings;
